import {
  deriveTripFulfillment,
  voucherStatusName,
  type TripFulfillment,
  type TripView,
  type VoucherStatus,
  type VoucherStatusName,
} from '@voll-intelligence/domain';

export type ModalKind = 'FLIGHT' | 'HOTEL' | 'BUS' | 'RENTAL' | 'MOBILITY';
export const MODAL_ORDER: ModalKind[] = ['FLIGHT', 'HOTEL', 'BUS', 'RENTAL', 'MOBILITY'];
/** Type guard: `voucher.modal` chega como `string` solto da fonte — isto é o
 *  único lugar que decide se ele é um dos 5 modais reconhecidos. */
export function isModal(s: string): s is ModalKind {
  return (MODAL_ORDER as string[]).includes(s);
}

/** CONCIERGE_* colapsa no modal base. O espelho do customer-api entrega o
 *  `voucher.modal` CRU do legado — um voo emitido pelo CS chega como
 *  `CONCIERGE_FLIGHT` — e normaliza só o `details.kind`. O que não está aqui nem em
 *  `MODAL_ORDER` (CONCIERGE_OTHERS, PAYMENT, EXPENSE…) não tem lugar na viagem. */
const CONCIERGE_BASE: Readonly<Record<string, ModalKind>> = {
  CONCIERGE_FLIGHT: 'FLIGHT',
  CONCIERGE_HOTEL: 'HOTEL',
  CONCIERGE_BUS: 'BUS',
};

/** O `voucher.modal` cru → o `ModalKind` que a tela conhece; `null` quando ele não
 *  tem lugar na viagem. Tolera caixa e espaço do produtor legado. É POR AQUI que
 *  todo leitor de `voucher.modal` e de `trip.modals` passa: `isModal` sozinho
 *  recusa o concierge, e foi assim que o voo VOLL-CF17316024 sumiu da viagem em
 *  que estava vinculado (2026-09-18) — a viagem na sidebar, o serviço em nenhum
 *  lugar. */
export function canonicalModal(modal: string | null | undefined): ModalKind | null {
  if (!modal) return null;
  const upper = modal.trim().toUpperCase();
  if (isModal(upper)) return upper;
  return CONCIERGE_BASE[upper] ?? null;
}

/** A lista declarada em `trip.modals` — crua, vinda das orders do trip-api — na
 *  ordem da tela e sem repetição: FLIGHT e CONCIERGE_FLIGHT viram UMA entrada. */
export function canonicalModals(modals: readonly string[]): ModalKind[] {
  const present = new Set<ModalKind>();
  for (const modal of modals) {
    const kind = canonicalModal(modal);
    if (kind) present.add(kind);
  }
  return MODAL_ORDER.filter((kind) => present.has(kind));
}

// A regra de voucher morto é UMA só, do domain (a trip-api lê a mesma); o re-export
// mantém `trip-document` como a porta única das telas.
export { isDeadVoucherStatus } from '@voll-intelligence/domain';

/** A situação da emissão da viagem (`TripFulfillment`), a pergunta "o que foi
 *  contratado está garantido?" — o eixo ao lado do ciclo de vida. Lê o que a
 *  trip-api carimbou; produtor antigo (campo AUSENTE) cai na MESMA regra do
 *  domain sobre os mesmos `vouchers`, então as duas respostas não divergem. Um
 *  `null` carimbado é resposta ("nada a prometer"), não ausência. */
export function tripFulfillment(
  trip: Pick<TripView, 'fulfillment' | 'vouchers'>,
): TripFulfillment | null {
  return trip.fulfillment === undefined ? deriveTripFulfillment(trip.vouchers) : trip.fulfillment;
}

/** Variantes tonais do `.vj-badge--*` usadas pelo pill de status do voucher. */
export type VoucherBadgeVariant = 'confirmed' | 'progress' | 'amber' | 'cancelled' | 'gray';

/** A cor de cada nome: as mesmas de antes para o mesmo estado. */
const NAME_VARIANT: Record<VoucherStatusName, VoucherBadgeVariant> = {
  REQUESTED: 'progress',
  WAITING_FOR_QUOTATION: 'progress',
  WAITING_FOR_EMISSION: 'progress',
  AWAITING_APPROVAL: 'amber',
  CANCELLATION_REQUESTED: 'amber',
  CHANGE_REQUESTED: 'amber',
  EMITTED: 'confirmed',
  CANCELED: 'cancelled',
  // Expirado é degrau RUIM na escada do protótipo (handoff Trip V2, 2026-08-05):
  // negativo com ação (cotar de novo), a mesma tinta do cancelado — o cinza de
  // antes lia como neutro, e neutro não pede correção.
  EXPIRED: 'cancelled',
  REPROVED: 'cancelled',
  REQUEST_DENIED: 'cancelled',
};

/** O que a pill lê do voucher além do status canônico. */
type PillVoucher = Readonly<{
  modal: string | null | undefined;
  cancellationRequestedAt?: Date | null;
  internalStatus?: string | null;
  tags?: readonly string[] | null;
}>;

/** Pill de status do voucher (variante `.vj-badge--*` + chave i18n). O nome é o da
 *  VOLL antiga, lido do `internalStatus` cru (`voucherStatusName`); o card de status do voucher
 *  (`voucherStatusCard`) usa a mesma chave, então a viagem e o voucher nunca dizem duas
 *  palavras sobre o mesmo estado. Null quando não há status. */
export function voucherStatusPill(
  status: VoucherStatus | null | undefined,
  voucher?: PillVoucher | null,
): { variant: VoucherBadgeVariant; labelKey: string } | null {
  const name = voucherStatusName({
    modal: voucher?.modal,
    status,
    internalStatus: voucher?.internalStatus,
    cancellationRequestedAt: voucher?.cancellationRequestedAt,
    tags: voucher?.tags,
  });
  return name ? { variant: NAME_VARIANT[name], labelKey: `trips.voucherStatus.${name}` } : null;
}
